import { useIntegrationHeartbeat } from '../features/integrations/useIntegrationHeartbeat';
import { HeartbeatChannelIcon } from '../features/integrations/HeartbeatChannelIcon';
import '../features/integrations/heartbeat.css';
import React, { useState, useRef, useEffect } from 'react';
import { formatSafeDateTime } from '../utils/dateUtils';
import {
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  ShieldCheck,
  Radio,
  ExternalLink,
  ChevronDown,
  X,
  Settings,
  FileDown
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const WhatsAppHeartbeatIndicator: React.FC = () => {
  const { data, isFetching, checkMutation, summary, channelList, hasChannels, isHealthy, isChecking } = useIntegrationHeartbeat();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Fecha o dropdown se clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);


  return (
    <div className="relative inline-block" ref={popoverRef}>
      {/* Botão de Status Global no Topbar */}
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all cursor-pointer shadow-sm ${
          !hasChannels
            ? 'bg-white/5 hover:bg-white/10 border-white/10 text-white/70 hover:border-white/20'
            : isHealthy
              ? 'bg-emerald-950/40 hover:bg-emerald-900/50 border-emerald-500/30 text-emerald-300 hover:border-emerald-500/50 shadow-emerald-950/20'
              : summary.operational > 0
                ? 'bg-amber-950/40 hover:bg-amber-900/50 border-amber-500/35 text-amber-300 hover:border-amber-500/55'
                : 'bg-rose-950/50 hover:bg-rose-900/60 border-rose-500/40 text-rose-300 hover:border-rose-500/60 shadow-rose-950/20 animate-pulse'
        }`}
        title="Monitor de conectividade das integrações deste tenant"
      >
        {/* Ícone Indicador */}
        <div className="relative flex items-center justify-center">
          <Radio
            size={15}
            className={
              !hasChannels
                ? 'text-white/40'
                : isHealthy
                  ? 'text-emerald-400'
                  : summary.operational > 0
                    ? 'text-amber-400'
                    : 'text-rose-400'
            }
          />
          {hasChannels && (
            <span
              className={`absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full ${
                isHealthy ? 'bg-emerald-400 animate-ping' : summary.operational > 0 ? 'bg-amber-400 animate-ping' : 'bg-rose-500 animate-ping'
              }`}
            />
          )}
        </div>

        {/* Texto do Status */}
        <div className="flex items-center gap-1.5 text-xs font-bold tracking-tight">
          <span className="hidden md:inline">Integrações</span>
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.2 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
            !hasChannels
              ? 'bg-white/10 text-white/60 border border-white/10'
              : isHealthy
                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                : summary.operational > 0
                  ? 'bg-amber-500/20 text-amber-200 border border-amber-500/30'
                  : 'bg-rose-500/25 text-rose-200 border border-rose-500/40'
          }`}>
            {isChecking ? (
              <>
                <RefreshCw size={9} className="animate-spin" />
                <span>Testando</span>
              </>
            ) : !hasChannels ? (
              <span>Sem integrações</span>
            ) : isHealthy ? (
              <>
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 inline-block" />
                <span>{summary.operational}/{summary.total} Online</span>
              </>
            ) : (
              <>
                <AlertTriangle size={9} className="text-amber-400" />
                <span>{summary.operational}/{summary.total} Ativo</span>
              </>
            )}
          </span>
        </div>

        <ChevronDown size={12} className={`transition-transform opacity-60 ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Popover / Card com STATUS INDIVIDUAL DOS CANAIS DO TENANT */}
      {open && (
        <div
          className="integration-heartbeat-popover absolute left-0 sm:left-auto sm:right-0 mt-2 w-84 sm:w-[420px] rounded-2xl border border-white/15 bg-bg-panel/95 backdrop-blur-xl shadow-2xl p-4 z-50 text-white animate-in fade-in zoom-in-95 duration-150 max-h-[85vh] overflow-y-auto"
        >
          {/* Header do Popover */}
          <div className="flex items-center justify-between pb-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-400">
                <Radio size={16} className={hasChannels && isHealthy ? 'animate-pulse' : ''} />
              </div>
              <div>
                <h4 className="text-xs font-bold text-white flex items-center gap-1.5">
                  Conectividade das Integrações
                </h4>
                <p className="text-[10px] text-white/50">
                  {data?.tenant_id ? `Tenant: ${data.tenant_id} • ` : ''}Canais configurados
                </p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          {/* Banner Resumo */}
          <div className="my-3 p-3 rounded-xl bg-white/5 border border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className={`p-2 rounded-lg ${
                !hasChannels
                  ? 'bg-white/10 text-white/60'
                  : isHealthy
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-amber-500/20 text-amber-400'
              }`}>
                {!hasChannels ? <Radio size={18} /> : isHealthy ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
              </div>
              <div>
                <strong className={`text-xs block ${!hasChannels ? 'text-white/80' : isHealthy ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {!hasChannels
                    ? 'Nenhuma integração configurada'
                    : isHealthy
                      ? 'Todas as Integrações Operacionais'
                      : `${summary.operational} de ${summary.total} Integrações Conectadas`}
                </strong>
                <span className="text-[10px] text-white/50">
                  {!hasChannels
                    ? 'Configure os canais Meta ou o Google Ads para monitorar a conexão.'
                    : isHealthy
                      ? 'Conectividade das integrações verificada'
                      : 'Há integrações que exigem verificação'}
                </span>
              </div>
            </div>
          </div>

          {/* Cards Dinâmicos Segregados por Tenant */}
          {channelList.length === 0 ? (
            <div className="my-3 p-5 rounded-xl bg-white/5 border border-white/10 text-center space-y-2.5">
              <div className="p-3 rounded-xl bg-white/5 text-white/40 w-fit mx-auto">
                <Radio size={22} />
              </div>
              <div>
                <h5 className="text-xs font-bold text-white">Nenhuma integração configurada para este tenant</h5>
                <p className="text-[11px] text-white/50 mt-1">
                  Adicione canais Meta ou uma conta Google Ads nas configurações.
                </p>
              </div>
              <div className="mt-2 flex items-center justify-center gap-2 flex-wrap">
                <button
                  onClick={() => {
                    setOpen(false);
                    navigate('/channels');
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-primary text-white text-xs font-bold hover:bg-brand-primary-hover transition-all cursor-pointer shadow-sm"
                >
                  <Settings size={13} />
                  <span>Configurar Canais</span>
                </button>
                <button onClick={() => { setOpen(false); navigate('/integrations'); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-primary text-white text-xs font-bold hover:bg-brand-primary-hover">
                  <Settings size={13} /> Google Ads
                </button>
                <a
                  href="/Guia_Credenciais_Meta_WhatsApp.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  download="Guia_Criacao_Credenciais_Meta_Ads.pdf"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/15 border border-white/15 text-white/90 hover:text-white text-xs font-semibold transition-all cursor-pointer shadow-sm"
                  title="Baixar roteiro em PDF para criação de credenciais Meta"
                >
                  <FileDown size={13} className="text-brand-primary" />
                  <span>Baixar Roteiro PDF</span>
                </a>
              </div>
            </div>
          ) : (
            <div className="space-y-2.5">
              {channelList.map((channel) => (
                <div
                  key={channel.id}
                  className={`p-2.5 rounded-xl border transition-all ${
                    channel.healthy
                      ? 'bg-white/5 border-white/10'
                      : 'bg-rose-500/10 border-rose-500/25'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      {<HeartbeatChannelIcon id={channel.id} provider={channel.provider} />}
                      <div>
                        <h5 className="text-xs font-bold text-white leading-none">{channel.name}</h5>
                        <span className="text-[10px] text-white/50">{channel.details || 'Monitoramento em tempo real'}</span>
                      </div>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                      channel.healthy
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                        : 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                    }`}>
                      {channel.badge || (channel.healthy ? 'Online' : 'Erro')}
                    </span>
                  </div>
                  <div className="text-[11px] text-white/70 pl-8 space-y-0.5">
                    <div className="flex items-center justify-between">
                      <span>Identificador: <strong className="text-white">{channel.identifier || channel.account_name || channel.page_name || 'Conectado'}</strong></span>
                      {channel.quality && (
                        <span className="text-[10px] text-emerald-400 font-semibold">Qualidade: {channel.quality}</span>
                      )}
                    </div>
                    {channel.error && (
                      <p className="text-rose-300 text-[10px] font-medium mt-1">⚠️ {channel.error}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Status Auto-Cura e Última Verificação */}
          <div className="mt-3 pt-2.5 border-t border-white/10 space-y-1 text-[10px] text-white/40">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1 text-emerald-400 font-semibold">
                <ShieldCheck size={12} /> Auto-Cura de Webhooks Meta
              </span>
              <span>Cron a cada 5m</span>
            </div>
            {data?.timestamp && (
              <div className="flex items-center justify-between">
                <span>Último Batimento:</span>
                <span>{formatSafeDateTime(data.timestamp)}</span>
              </div>
            )}
          </div>

          {/* Ações */}
          <div className="pt-3 mt-2 border-t border-white/10 flex items-center gap-2">
            <button
              onClick={() => checkMutation.mutate()}
              disabled={checkMutation.isPending || isFetching || !hasChannels}
              className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-brand-primary hover:bg-brand-primary-hover py-2 px-3 text-xs font-bold text-white transition-all cursor-pointer disabled:opacity-50 shadow-md shadow-brand-primary/20"
            >
              <RefreshCw size={13} className={checkMutation.isPending || isFetching ? 'animate-spin' : ''} />
              <span>{checkMutation.isPending ? 'Testando Conexões...' : hasChannels ? 'Verificar Conexões' : 'Sem Integrações para Testar'}</span>
            </button>

            <button
              onClick={() => {
                setOpen(false);
                navigate('/conversations');
              }}
              className="flex items-center justify-center gap-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 py-2 px-3 text-xs font-semibold text-white/80 transition-colors cursor-pointer"
              title="Abrir Atendimentos"
            >
              <span>Atendimentos</span>
              <ExternalLink size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
